import React from 'react';
import { Card, Tag, Space, Typography, Tooltip, Progress } from 'antd';
import { 
  CheckCircleOutlined, 
  CloseCircleOutlined, 
  InfoCircleOutlined,
  UserOutlined,
  SafetyOutlined
} from '@ant-design/icons';

const { Text, Title } = Typography;

/**
 * Permission Summary Component
 * Shows a quick overview of user permissions by category
 */
const PermissionSummary = ({ 
  username, 
  userRole, 
  permissions = {}, 
  availablePermissions = {},
  compact = false 
}) => {
  // Calculate permission statistics by category
  const getPermissionStats = () => {
    const stats = {};
    let totalPermissions = 0;
    let totalGranted = 0;

    Object.entries(availablePermissions).forEach(([category, categoryPermissions]) => {
      const categoryKeys = Object.keys(categoryPermissions);
      const grantedInCategory = categoryKeys.filter(key => permissions[key]).length;
      
      stats[category] = {
        total: categoryKeys.length,
        granted: grantedInCategory,
        percentage: categoryKeys.length > 0 ? Math.round((grantedInCategory / categoryKeys.length) * 100) : 0
      };

      totalPermissions += categoryKeys.length;
      totalGranted += grantedInCategory;
    });

    return {
      categories: stats,
      overall: {
        total: totalPermissions,
        granted: totalGranted,
        percentage: totalPermissions > 0 ? Math.round((totalGranted / totalPermissions) * 100) : 0
      }
    };
  };

  const stats = getPermissionStats();

  const getStatusColor = (percentage) => {
    if (percentage >= 80) return 'success';
    if (percentage >= 50) return 'normal';
    if (percentage >= 25) return 'active';
    return 'exception';
  };

  const getTagColor = (percentage) => {
    if (percentage >= 80) return 'green';
    if (percentage >= 50) return 'blue';
    if (percentage >= 25) return 'orange';
    return 'red';
  };

  if (compact) {
    return (
      <Space size="small">
        <Tag color="blue">{userRole}</Tag>
        {userRole === 'Admin' ? (
          <Tag color="gold" icon={<SafetyOutlined />}>All Permissions</Tag>
        ) : (
          <Tag color={getTagColor(stats.overall.percentage)}>
            {stats.overall.granted}/{stats.overall.total} permissions
          </Tag>
        )}
      </Space>
    );
  }

  return (
    <Card
      title={
        <Space>
          <UserOutlined />
          <Title level={5} style={{ margin: 0 }}>Permission Summary</Title>
        </Space>
      }
      size="small"
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {/* User Info */}
        <div>
          <Space>
            <Text strong>User:</Text>
            <Text>{username}</Text>
            <Text strong>Role:</Text>
            <Tag color="blue">{userRole}</Tag>
          </Space>
        </div>

        {/* Overall Progress */}
        {userRole === 'Admin' ? (
          <div>
            <Space>
              <SafetyOutlined style={{ color: '#faad14' }} />
              <Text strong>Administrator - All Permissions Granted</Text>
            </Space>
          </div>
        ) : (
          <div>
            <div style={{ marginBottom: '8px' }}>
              <Space>
                <Text strong>Overall Access:</Text>
                <Text>{stats.overall.granted} of {stats.overall.total} permissions</Text>
                <Text type="secondary">({stats.overall.percentage}%)</Text>
              </Space>
            </div>
            <Progress
              percent={stats.overall.percentage}
              status={getStatusColor(stats.overall.percentage)}
              size="small"
            />
          </div>
        )}

        {/* Category Breakdown */}
        {userRole !== 'Admin' && Object.keys(stats.categories).length > 0 && (
          <div>
            <Text strong style={{ marginBottom: '8px', display: 'block' }}>
              Access by Category:
            </Text>
            <Space wrap>
              {Object.entries(stats.categories).map(([category, categoryStats]) => (
                <Tooltip
                  key={category}
                  title={`${categoryStats.granted} of ${categoryStats.total} permissions granted`}
                >
                  <Tag color={getTagColor(categoryStats.percentage)}>
                    {category}: {categoryStats.percentage}%
                  </Tag>
                </Tooltip>
              ))}
            </Space>
          </div>
        )}

        {/* Key Permissions Status */}
        {userRole !== 'Admin' && (
          <div>
            <Text strong style={{ marginBottom: '8px', display: 'block' }}>
              Key Permissions:
            </Text>
            <Space wrap>
              {[
                'patients.view',
                'clinical.problems.manage',
                'admin.users.manage',
                'reports.view'
              ].map(key => {
                const hasPermission = permissions[key];
                return (
                  <Tag
                    key={key}
                    color={hasPermission ? 'green' : 'red'}
                    icon={hasPermission ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                  >
                    {key.split('.').pop()}
                  </Tag>
                );
              })}
            </Space>
          </div>
        )}
      </Space>
    </Card>
  );
};

export default PermissionSummary;
